// pages/public/FreeReport.jsx
// Shows free topline report after assessment
// Individual users see Pay to Unlock CTA
// Enterprise and shared link users see report delivered message

import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAssessment } from '../../context/AssessmentContext';
import axios from 'axios';

// Score colour tiers (matches admin scorecard and PDF report)
//   green  = strong        (21+)
//   yellow = comfort zone  (16-20)
//   red    = discomfort    (below 16)
const scoreTextColor = (v) => (v >= 16 ? '#B7950B' : '#E74C3C');

const DIMENSION_NAMES = {
  goalOrientation: { name: 'Goal Orientation', left: 'Intentional', right: 'Adapting' },
  mission: { name: 'Mission', left: 'Shake Things Up', right: 'Maintain Stability' },
  relationships: { name: 'Relationships', left: 'Keep Formal Distance', right: 'Develop Casual Bonds' },
  reciprocity: { name: 'Reciprocity', left: 'Contribute Value', right: 'Learn & Ask for Help' },
  decisionMaking: { name: 'Decision Making', left: 'Impose', right: 'Facilitate' },
  pace: { name: 'Pace', left: 'Go Fast', right: 'Go Slow' },
  succession: { name: 'Succession', left: 'Replace/Clean Out', right: 'Develop Existing Talent' },
  loyalty: { name: 'Loyalty', left: 'Serve Hierarchy', right: 'Serve Team' },
};

const FreeReport = () => {
  const { submissionId } = useParams();
  const navigate = useNavigate();
  const { user, sessionToken } = useAssessment();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [retries, setRetries] = useState(0);
  const [paying, setPaying] = useState(false);
  const [payConfig, setPayConfig] = useState(null);
  const [payError, setPayError] = useState('');

  // Same reason as ResultsPage: the PayPal button renders once and its
  // callbacks would otherwise hold whatever the session was at that instant.
  // This page is the one the $149 email button opens, so it is routinely
  // reached on a second device with no stored session at all.
  const checkoutRef = useRef({ userId: null, sessionToken: null, submissionId });
  useEffect(() => {
    checkoutRef.current = {
      userId: user?.id || data?.user?._id || null,
      sessionToken,
      submissionId,
    };
  }, [user, sessionToken, data, submissionId]);

  useEffect(() => {
    fetchReport();
    fetchPayConfig();
  }, [submissionId]);

  const fetchReport = async () => {
    try {
      const response = await axios.get(`/reports/free/${submissionId}`);
      if (response.data.success) {
        const reportData = response.data.data.report;
        if (!reportData.freeContent && retries < 10) {
          setTimeout(() => {
            setRetries(r => r + 1);
            fetchReport();
          }, 3000);
          return;
        }
        setData(reportData);
      }
    } catch (err) {
      console.error('Report fetch failed:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchPayConfig = async () => {
    try {
      const response = await axios.get('/payment/config');
      if (response.data.success) {
        setPayConfig(response.data.data);
      }
    } catch (err) {
      console.error('Pay config fetch failed:', err);
    }
  };

  // Load PayPal SDK when config is ready
  useEffect(() => {
    if (payConfig?.paypalConnected && payConfig?.paypalClientId && data) {
      loadPayPalSDK(payConfig.paypalClientId);
    }
  }, [payConfig, data]);

  const loadPayPalSDK = (clientId) => {
    const existingScript = document.getElementById('paypal-sdk');
    if (existingScript) {
      renderPayPalButton();
      return;
    }
    const script = document.createElement('script');
    script.id = 'paypal-sdk';
    script.src = `https://www.paypal.com/sdk/js?client-id=${clientId}&currency=USD`;
    script.onload = () => renderPayPalButton();
    document.body.appendChild(script);
  };

  const renderPayPalButton = () => {
    if (!window.paypal) return;
    const container = document.getElementById('paypal-button-container');
    if (!container) return;

    window.paypal.Buttons({
      style: { layout: 'vertical', color: 'gold', shape: 'rect', label: 'pay', height: 44 },
      createOrder: async () => {
        const response = await axios.post('/payment/create-order', checkoutRef.current);
        return response.data.data.orderId;
      },
      onApprove: async (approval) => {
        setPaying(true);
        try {
          const response = await axios.post(
            `/payment/capture/${approval.orderID}`,
            checkoutRef.current
          );
          if (response.data.success) {
            // /report/unlocked/:submissionId is not a route. It fell through to
            // the catch-all and dumped the person on the home page straight
            // after they had paid $149, with no confirmation of any kind.
            setPayError('');
            fetchReport();
            window.scrollTo({ top: 0, behavior: 'smooth' });
          }
        } catch (err) {
          setPayError('We could not confirm the payment. If you were charged, please write to info@execspringboard.com before trying again.');
        } finally {
          setPaying(false);
        }
      },
      onError: () => setPayError('Payment could not be started. Please try again.'),
      onCancel: () => setPayError('Payment was cancelled.'),
    }).render('#paypal-button-container');
  };

  if (loading || (!data && retries < 10)) {
    return (
      <div style={s.loadingPage}>
        <div style={s.spinner} />
        <h2 style={s.loadingTitle}>Generating Your Report</h2>
        <p style={s.loadingText}>Analysing your responses across 8 leadership dimensions...</p>
      </div>
    );
  }

  const scores = data?.dimensionScores || {};
  const dimensions = Object.entries(scores);
  const freeContent = data?.freeContent || '';
  const userAccessType = data?.user?.accessType || 'individual';
  const isIndividual = userAccessType === 'individual';

  return (
    <div style={s.page}>
      <div style={s.header}>
        <div style={s.logo}>
          <span style={s.logoExec}>EXECUTIVE</span>
          <span style={s.logoSpring}>Springboard</span>
        </div>
      </div>

      <div style={s.container}>
        {/* Success Banner */}
        <div style={s.successBanner}>
          <div style={s.successIcon}>✅</div>
          <div>
            {/* Steve: "Your Leadership Snapshot is Ready" was a peculiar headline
                when the report sits right below it. The line that used to be the
                subtitle is now the headline. The "within 4 to 8 hours" sentence is
                gone because it read as though delivery were automatic. */}
            <h1 style={s.successTitle}>
              Here are your free assessment results, {data?.user?.firstName}.
            </h1>
            {isIndividual && (
              <p style={s.successText}>Unlock your comprehensive report below.</p>
            )}
          </div>
        </div>

        {/* AI Generated Content */}
        {freeContent && (
          <div style={s.card}>
            <h2 style={s.cardTitle}>Your Leadership Profile</h2>
            <div style={s.reportContent}>
              {freeContent.split('\n').map((line, i) => {
                if (line.match(/^[1-9]\.|^PROFILE|^TOP 3|^SUMMARY|^WHAT IS/i)) {
                  return <h3 key={i} style={s.reportSection}>{line}</h3>;
                }
                if (line.trim() === '') return <br key={i} />;
                return <p key={i} style={s.reportPara}>{line}</p>;
              })}
            </div>
          </div>
        )}

        {/* Scorecard */}
        {dimensions.length > 0 && (
          <div style={s.card}>
            <h2 style={s.cardTitle}>Your Comfort Zone Scorecard</h2>
            <p style={s.cardSubtitle}>16 or above is a comfort zone. Below 16 is a potential derailer.</p>
            <div style={s.scoreLegend}>
              <span style={s.legendItem}><span style={{ ...s.legendSwatch, backgroundColor: '#F1C40F' }} /> Comfort zone (16+)</span>
              <span style={s.legendItem}><span style={{ ...s.legendSwatch, backgroundColor: '#E74C3C' }} /> Potential derailer (below 16)</span>
            </div>
            <div style={s.scorecardTable}>
              <div style={s.scorecardHeader}>
                <span>Dimension</span>
                <span style={{ textAlign: 'center' }}>Left Pole</span>
                <span style={{ textAlign: 'center' }}>Score</span>
                <span style={{ textAlign: 'center' }}>Score</span>
                <span style={{ textAlign: 'center' }}>Right Pole</span>
              </div>
              {dimensions.map(([dimId, scoreData]) => {
                const dimInfo = DIMENSION_NAMES[dimId];
                if (!dimInfo) return null;
                return (
                  <div key={dimId} style={s.scorecardRow}>
                    <span style={s.dimName}>{dimInfo.name}</span>
                    <span style={s.poleName}>{dimInfo.left}</span>
                    <span style={{ ...s.scoreCell, color: scoreTextColor(scoreData.leftScore) }}>
                      {scoreData.leftScore}
                    </span>
                    <span style={{ ...s.scoreCell, color: scoreTextColor(scoreData.rightScore) }}>
                      {scoreData.rightScore}
                    </span>
                    <span style={s.poleName}>{dimInfo.right}</span>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Individual: Pay to Unlock CTA */}
        {isIndividual && (
          <div id="upgrade" style={s.upgradeCta}>
            <div style={s.upgradeIcon}>🔓</div>
            <h2 style={s.upgradeTitle}>Want to dig deeper with a comprehensive report?</h2>
            <p style={s.upgradeText}>
              Your comprehensive report includes everything you need to use the assessment to
              understand how your company's needs align with your comfort zone and to develop
              your leadership effectiveness:
            </p>

            <div style={s.upgradeFeatures}>
              {[
                'Complete dimension by dimension analysis across all 8 areas',
                'Exception report highlighting your highest tension areas',
                'Personalized 90 day development roadmap',
                '30 minute one-on-one analytic consultation with Executive Springboard president and Comfort Zone Assessment author Steve Moss',
              ].map((f, i) => (
                <div key={i} style={s.upgradeFeature}>
                  <span style={s.upgradeCheck}>✓</span> {f}
                </div>
              ))}
            </div>

            <div style={s.priceBox}>
              <span style={s.priceAmount}>${payConfig?.assessmentPrice || 149}</span>
              <span style={s.priceNote}>one time payment</span>
            </div>

            {paying && (
              <div style={s.payingBox}>
                <div style={s.spinnerSmall} />
                <p style={{ color: '#fff', margin: 0 }}>Processing payment...</p>
              </div>
            )}

            {payConfig?.paypalConnected ? (
              <div style={s.paypalWrap}>
                {payError && <div style={s.payError}>{payError}</div>}
                <p style={s.secureNote}>🔒 Secure payment powered by PayPal</p>
                <div id="paypal-button-container" style={{ maxWidth: '360px', margin: '0 auto' }} />
              </div>
            ) : (
              <div style={s.notConfigured}>
                <p style={s.notConfiguredText}>
                  Payment system is being configured. Please check back shortly.
                </p>
              </div>
            )}
          </div>
        )}

        {/* Enterprise / Shared Link: Report coming message */}
        {!isIndividual && (
          <div style={s.comingBox}>
            <div style={s.comingIcon}>📬</div>
            <h3 style={s.comingTitle}>Your Full Report is Being Prepared</h3>
            <p style={s.comingText}>
              Your detailed leadership report will be delivered to <strong>{data?.user?.email}</strong> within 4 to 8 hours after review by the Executive Springboard team.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

const s = {
  page: { minHeight: '100vh', backgroundColor: 'var(--color-off-white)', fontFamily: 'var(--font-primary)' },
  loadingPage: { minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '16px', textAlign: 'center', padding: '24px' },
  spinner: { width: '48px', height: '48px', border: '5px solid var(--color-light-gray)', borderTopColor: 'var(--color-teal)', borderRadius: '50%', animation: 'spin 0.8s linear infinite', margin: '0 auto' },
  spinnerSmall: { width: '24px', height: '24px', border: '3px solid rgba(255,255,255,0.3)', borderTopColor: '#fff', borderRadius: '50%', animation: 'spin 0.8s linear infinite' },
  loadingTitle: { fontSize: '24px', fontWeight: '700', color: 'var(--color-primary)', margin: 0 },
  loadingText: { fontSize: '15px', color: 'var(--color-text-light)', maxWidth: '400px' },
  header: { backgroundColor: 'var(--color-primary)', padding: '0 24px', height: '64px', display: 'flex', alignItems: 'center' },
  logo: { display: 'flex', flexDirection: 'column', gap: '1px' },
  logoExec: { fontSize: '9px', fontWeight: '700', letterSpacing: '3px', color: 'var(--color-teal)', lineHeight: 1 },
  logoSpring: { fontSize: '18px', fontWeight: '700', color: '#FFFFFF', lineHeight: 1 },
  container: { maxWidth: '760px', margin: '0 auto', padding: '40px 24px 80px' },
  successBanner: { display: 'flex', gap: '16px', alignItems: 'flex-start', backgroundColor: 'var(--color-success-bg)', border: '1px solid #A9DFBF', borderRadius: '12px', padding: '24px', marginBottom: '24px' },
  successIcon: { fontSize: '32px', flexShrink: 0 },
  successTitle: { fontSize: '22px', fontWeight: '700', color: 'var(--color-primary)', marginBottom: '6px' },
  successText: { fontSize: '15px', color: 'var(--color-text-light)', lineHeight: '1.6' },
  card: { backgroundColor: '#fff', borderRadius: '12px', border: '1px solid var(--color-border)', boxShadow: 'var(--shadow-sm)', padding: '32px', marginBottom: '20px' },
  cardTitle: { fontSize: '20px', fontWeight: '700', color: 'var(--color-primary)', marginBottom: '6px' },
  cardSubtitle: { fontSize: '14px', color: 'var(--color-text-light)', marginBottom: '12px' },
  scoreLegend: { display: 'flex', gap: '14px', flexWrap: 'wrap', marginBottom: '20px' },
  legendItem: { display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', color: 'var(--color-text-light)' },
  legendSwatch: { width: '11px', height: '11px', borderRadius: '3px', display: 'inline-block' },
  reportContent: { lineHeight: '1.7' },
  reportSection: { fontSize: '15px', fontWeight: '700', color: 'var(--color-primary)', marginTop: '20px', marginBottom: '8px', borderBottom: '1px solid var(--color-border)', paddingBottom: '6px' },
  reportPara: { fontSize: '14px', color: 'var(--color-text)', lineHeight: '1.7', marginBottom: '6px' },
  scorecardTable: { display: 'flex', flexDirection: 'column', gap: '4px' },
  scorecardHeader: { display: 'grid', gridTemplateColumns: '1.2fr 1fr 50px 50px 1fr', gap: '8px', padding: '8px 12px', fontSize: '11px', fontWeight: '600', color: 'var(--color-text-light)', textTransform: 'uppercase', letterSpacing: '1px' },
  scorecardRow: { display: 'grid', gridTemplateColumns: '1.2fr 1fr 50px 50px 1fr', gap: '8px', padding: '12px', borderRadius: '8px', backgroundColor: 'var(--color-off-white)', alignItems: 'center' },
  dimName: { fontSize: '13px', fontWeight: '600', color: 'var(--color-primary)' },
  poleName: { fontSize: '12px', color: 'var(--color-text-light)' },
  scoreCell: { fontSize: '16px', fontWeight: '700', textAlign: 'center' },
  upgradeCta: { backgroundColor: 'var(--color-primary)', borderRadius: '16px', padding: '40px', textAlign: 'center', color: '#fff', marginBottom: '20px' },
  upgradeIcon: { fontSize: '40px', marginBottom: '16px' },
  upgradeTitle: { fontSize: '26px', fontWeight: '700', color: '#fff', marginBottom: '12px' },
  upgradeText: { fontSize: '15px', color: 'rgba(255,255,255,0.75)', lineHeight: '1.7', marginBottom: '24px', maxWidth: '500px', margin: '0 auto 24px' },
  upgradeFeatures: { display: 'flex', flexDirection: 'column', gap: '8px', textAlign: 'left', maxWidth: '360px', margin: '0 auto 24px', backgroundColor: 'rgba(255,255,255,0.05)', borderRadius: '12px', padding: '20px 24px' },
  upgradeFeature: { fontSize: '14px', color: 'rgba(255,255,255,0.85)', display: 'flex', gap: '10px' },
  upgradeCheck: { color: 'var(--color-teal)', fontWeight: '700' },
  priceBox: { display: 'flex', alignItems: 'baseline', gap: '8px', justifyContent: 'center', marginBottom: '24px' },
  priceAmount: { fontSize: '48px', fontWeight: '800', color: '#fff' },
  priceNote: { fontSize: '14px', color: 'rgba(255,255,255,0.5)' },
  payingBox: { display: 'flex', alignItems: 'center', gap: '12px', justifyContent: 'center', padding: '16px', marginBottom: '12px' },
  paypalWrap: { maxWidth: '400px', margin: '0 auto' },
  payError: { backgroundColor: 'rgba(255,255,255,0.1)', color: '#FFB3B3', borderRadius: '8px', padding: '10px 14px', fontSize: '13px', marginBottom: '12px' },
  secureNote: { fontSize: '13px', color: 'rgba(255,255,255,0.5)', marginBottom: '12px' },
  notConfigured: { backgroundColor: 'rgba(255,255,255,0.05)', borderRadius: '10px', padding: '20px' },
  notConfiguredText: { fontSize: '14px', color: 'rgba(255,255,255,0.6)' },
  comingBox: { backgroundColor: '#fff', borderRadius: '16px', border: '1px solid var(--color-border)', padding: '40px', textAlign: 'center' },
  comingIcon: { fontSize: '40px', marginBottom: '16px' },
  comingTitle: { fontSize: '20px', fontWeight: '700', color: 'var(--color-primary)', marginBottom: '12px' },
  comingText: { fontSize: '15px', color: 'var(--color-text-light)', lineHeight: '1.7', maxWidth: '460px', margin: '0 auto' },
};

export default FreeReport;
